import { Component, ElementRef, OnInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { UsersService } from '../users.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { CaptchaComponent } from '../captcha/captcha.component';
import { TranslateService } from '@ngx-translate/core';
import { ValidationService } from 'src/app/shared/services/validation.service';
import { DomainsService } from 'src/app/shared/services/domains.service';

@Component({
	// changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
	@ViewChild('captchaComponent', { static: false }) captchaComponent: CaptchaComponent;
	@ViewChild('emailComponent', { static: false }) emailComponent: ElementRef;
	@ViewChildren('otpInput') otpInputList: QueryList<ElementRef>;
	isValidEmail = false;
	showCaptcha: boolean = false;
	
	// OTP flow state
	showOtp: boolean = false;
	otp: string[] = ['', '', '', '', '', ''];
	resendCountdown: number = 60;
	resendDisabled: boolean = true;
	resendTimer: any = null;
	pendingLoginPayload: any = null; // store email/password/captcha to re-submit after OTP
	maxAttemptsReached: boolean = false; // case 9: max verification attempts reached
	accountBlocked: boolean = false; // case 10: account blocked for 30 minutes
	blockTimeRemaining: number = 30; // minutes remaining for block
	maxResendAttemptsReached: boolean = false; // case 7: max resend attempts reached
	userEmail: string = ''; // store user's email for display
	otpJustResent: boolean = false; // track if OTP was just resent
	resendCount: number = 0; // track number of resends
	
	readonly initialResendWaitTime = 60; // 60 seconds for first time
	readonly subsequentResendWaitTime = 300; // 5 minutes for subsequent resends
	readonly otpLength = 6;

	copyrightYear = new Date().getFullYear();

	googlePlayAppLink: string = this.domainsService.mobileAppLinks.google_play.iotah;
	appStoreAppLink: string = this.domainsService.mobileAppLinks.app_store.iotah;
	siteName: string = this.translateService.instant('g.smart_telemetrics');
	imageClass: string = 'smart_telemetrics';

	constructor(
		private router: Router,
		private navigation: NavigationService,
		private usersService: UsersService,
		private notificationMessage: NotificationMessageService,
		private commonUtil: CommonService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private validationService: ValidationService,
		private domainsService: DomainsService,
	) {}

	yearAge = new Date().getFullYear();
	loginLogo: string = '';

	ngOnInit() {
		this.checkExpiredLink();
		this.updateLoginImage();

		const domainLinks = this.domainsService.getAppLinks();

		this.googlePlayAppLink = domainLinks.google_play;
		this.appStoreAppLink = domainLinks.app_store;
	}

	ngAfterViewInit() {
		if (!this.commonUtil.isTabletOrMobileScreen)
			this.emailComponent.nativeElement.focus();
	}

	checkExpiredLink() {
		this.route.queryParams.subscribe(params => {
			if('link_expired' in params)
				return this.notificationMessage.setMessage(this.translateService.instant('g.link_expired'));
		});
	}

	updateLoginImage() {
		if(this.domainsService.isChargLink) {
			this.imageClass = 'charglink';
			this.siteName = this.translateService.instant('g.charglink');
			this.loginLogo = '/images/chargLink/charglink-logo.png';
			return;
		}

		if(this.domainsService.isBattlink) {
			this.imageClass = 'battlink';
			this.siteName = this.translateService.instant('g.battlink');
			this.loginLogo = '/images/battLink/battlink-logo.png';
			return;
		}

		if(this.domainsService.isIotah) {
			this.imageClass = 'iotah';
			this.siteName = this.translateService.instant('g.iotah');
			this.loginLogo = '/images/logo-v1.png';
			return;
		}

		this.imageClass = 'smart_telemetrics';
		this.siteName = this.translateService.instant('g.smart_telemetrics');
		this.loginLogo = '/images/smartTelemetrics/logo.png';
		return;
	}

	onLogin(form: NgForm) {
		if (form.invalid) {
			Object.keys(form.controls).forEach((key) => {
				form.controls[key].markAsTouched();
			});
			return;
		}

		const email    = form.value.email;
		const password = form.value.password;
		const captcha_key = this.captchaComponent?.captchaKey;
		const captcha_text = this.captchaComponent?.captchaText;

		if(!email || !password)
			return;

		if(captcha_key && !captcha_text)
			return this.notificationMessage.setMessage('translate|login_register.too_many_incorrect_attempts_provide_captcha');

		this.login({ email, password, captcha_key, captcha_text });
	}

	/**
	 * OTP helpers
	 */
	startResendTimer() {
		this.resendDisabled = true;
		// Use 60 seconds for first time (resendCount === 0), 5 minutes for subsequent resends
		const waitTime = this.resendCount === 0 ? this.initialResendWaitTime : this.subsequentResendWaitTime;
		this.resendCountdown = waitTime;

		if (this.resendTimer)
			clearInterval(this.resendTimer);

		this.resendTimer = setInterval(() => {
			this.resendCountdown--;
			if (this.resendCountdown <= 0) {
				this.resendDisabled = false;
				clearInterval(this.resendTimer);
				this.resendTimer = null;
			}
		}, 1000);
	}

	stopResendTimer() {
		if (this.resendTimer) {
			clearInterval(this.resendTimer);
			this.resendTimer = null;
		}
	}

	getFormattedCountdown(): string {
		const minutes = Math.floor(this.resendCountdown / 60);
		const seconds = this.resendCountdown % 60;
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	resendLoginOtp() {
		if (!this.pendingLoginPayload)
			return;

		this.usersService.resendLoginOtp({email: this.pendingLoginPayload.email}).subscribe((res:any) => {
			switch (res?.api_status) {
				case 7:
					this.notificationMessage.setMessage('translate|otp.max_resend_attempts_reached');
					this.resendDisabled = true;
					this.maxResendAttemptsReached = true;
					this.stopResendTimer();
					break;
				case 12:
					// Code sent successfully but max resend attempts reached - hide resend button
					this.otpJustResent = true;
					this.resendCount++; // increment resend count
					this.maxResendAttemptsReached = true; // hide resend button
					this.stopResendTimer();
					this.resendDisabled = true;
					// Reset max attempts state when new code sent
					this.maxAttemptsReached = false;
					this.otp = ['', '', '', '', '', ''];
					setTimeout(()=> this.focusOtpInput(0), 100);
					break;
				default:
					// assume api returns success or instructs captcha/show message
					this.otpJustResent = true;
					this.resendCount++; // increment resend count
					this.startResendTimer();
					// Reset max attempts state when new code sent
					this.maxAttemptsReached = false;
					this.otp = ['', '', '', '', '', ''];
					setTimeout(()=> this.focusOtpInput(0), 100);
					break;
			}
		});
	}

	focusOtpInput(idx:number){
		const list = this.otpInputList?.toArray();
		if(list && list[idx])
			(list[idx].nativeElement as HTMLInputElement).focus();
	}

	handleOtpInput(event: any, index: number) {
		const val = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
		this.otp[index] = val.slice(-1);

		if (val && index < 5)
			this.focusOtpInput(index + 1);

		if (!val && index > 0)
			this.focusOtpInput(index - 1);
	}

	handleOtpPaste(event: ClipboardEvent, index: number) {
		event.preventDefault();
		const pastedData = event.clipboardData?.getData('text').replace(/[^0-9]/g, '');
		
		if (!pastedData)
			return;

		// Split pasted data and fill inputs starting from current index
		const digits = pastedData.split('').slice(0, this.otpLength - index);
		digits.forEach((digit, i) => {
			if (index + i < this.otpLength) {
				this.otp[index + i] = digit;
			}
		});

		// Focus the next empty input or the last filled input
		const nextIndex = Math.min(index + digits.length, this.otpLength - 1);
		setTimeout(() => this.focusOtpInput(nextIndex), 10);
	}

	handleOtpKeydown(event: KeyboardEvent, index:number) {
		if (event.key === 'Backspace' && !this.otp[index] && index > 0)
			this.focusOtpInput(index - 1);
	}

	verifyOtp() {
		const code = this.otp.join('');
		if (code.length !== this.otpLength)
			return this.notificationMessage.setMessage('translate|otp.invalid_otp', {clearOnXTimeNavigate:1});

		// call verify-login-otp-code api first (as requested). If success, re-call login with otp
		this.usersService.verifyLoginOtpCode({ email: this.pendingLoginPayload.email, code }).subscribe((res: any) => {
			switch (res?.api_status) {
				case 8:
					this.notificationMessage.setMessage('translate|otp.invalid_otp');
					this.otp = ['', '', '', '', '', ''];
					break;
				case 9:
					// Max attempts reached for current code: hide inputs/verify, show resend-only UI
					this.maxAttemptsReached = true;
					this.stopResendTimer();
					this.resendDisabled = false; // enable resend immediately
					this.otp = ['', '', '', '', '', ''];
					this.notificationMessage.setMessage('translate|otp.max_attempts_reached');
					break;
				case 10:
					// Account blocked for 30 minutes: reset state and return to login form
					this.stopResendTimer();
					this.showOtp = false;
					this.otp = ['', '', '', '', '', ''];
					this.maxAttemptsReached = false;
					this.accountBlocked = false;
					this.resendDisabled = true;
					this.pendingLoginPayload = null;
					this.notificationMessage.setMessage(this.translateService.instant('login_register.login_blocked_try_after_x_minutes', {counter: this.blockTimeRemaining}));
					break;
				case 11:
					this.notificationMessage.setMessage('translate|otp.otp_expired');
					this.otp = ['', '', '', '', '', ''];
					this.stopResendTimer();
					this.resendDisabled = false; // enable resend immediately
					break;
				default:
					this.login({ email: this.pendingLoginPayload.email, password: this.pendingLoginPayload.password }, true);
					break;
			}
		});
	}

	isOtpComplete(): boolean {
		return this.otp.join('').length === this.otpLength && this.otp.every(c => c !== undefined && c !== null && c !== '');
	}

	setAppropriteFailedMessage(response){
		if(response?.shouldBlockLogin)
			return this.notificationMessage.setMessage(
				this.translateService.instant('login_register.login_blocked_try_after_x_minutes', {counter: response.timeRemaining}));

		if(response?.shouldShowCaptcha)
			return this.notificationMessage.setMessage('translate|login_register.too_many_incorrect_attempts_provide_captcha');

		return this.notificationMessage.setMessage('translate|login_register.invalid_username_or_password', {clearOnXTimeNavigate: 1});
	}

	onSuccessLogin(loginResponse) {
		loginResponse.access_functions			= this.commonUtil.revertPermissionValue(loginResponse.access_functions);
		loginResponse.permissions.permission	= this.commonUtil.revertPermissionValue(loginResponse.permissions.permission);
		this.usersService.setCurrentUser(loginResponse);

		if(loginResponse.force_password_change)
			return this.router.navigate(['/change-expired-password']);

		let url = this.navigation.getPreviousUrl();
		if(url && !['/landing', '/contact'].includes(url))
			this.router.navigateByUrl(url)
		else
			this.router.navigate(['/'])
	}

	checkEmailValidation() {
		const email = this.emailComponent.nativeElement.value;
		this.isValidEmail = this.validationService.dataValidator('email', {allowEmpty: true}, email);
	}

	login(payload, fromVerifyOtp = false) {
		this.usersService.login(payload).subscribe(
			(res: any) => {
				this.notificationMessage.closeNotification();

				// Special-case: backend indicates OTP required with api_status === 1
				if (!fromVerifyOtp && !res.api_status && res.has_2sv) {
					this.pendingLoginPayload = payload;
					this.userEmail = payload.email;
					this.otpJustResent = false;
					this.resendCount = 0; // reset resend count for new login
					this.showOtp = true;
					this.startResendTimer();
					return;
				}

				if(res.api_status){
					switch (res.api_status) {
						case 2:
							this.setAppropriteFailedMessage(res.response);
							break;
						case 3:
							return this.router.navigate(['/non-confirmed-user-email', res.response.id]);
						case 4:
							this.notificationMessage.setMessage('translate|login_register.please_check_your_email', {type: 'info'});
							break;
						case 6:
							this.notificationMessage.setMessage('translate|login_register.account_deactivated', {type: 'danger'});
							break;
						case 5:
							this.notificationMessage.setMessage('translate|users.prevent_to_login_err_msg', {type: 'danger'});
							break;
					}

					if(res.response?.shouldShowCaptcha){
						this.captchaComponent?.getCaptcha(true);
						this.showCaptcha = true;
					}
				} else {
					if (!fromVerifyOtp) {
						this.showOtp = false;
						this.stopResendTimer();
					}
					this.onSuccessLogin(res);
				}
			}
		);

	}
}
